import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

const AdminNotificationContext = createContext(null);

export const useAdminNotifications = () => {
  const context = useContext(AdminNotificationContext);
  if (!context) {
    throw new Error('useAdminNotifications must be used within AdminNotificationProvider');
  }
  return context;
};

const EVENT_LABELS = {
  new_user_signup: 'New User Signup',
  wallet_address_copied: 'Wallet Address Copied',
  deposit_submitted: 'Deposit Submitted',
  withdrawal_requested: 'Withdrawal Requested',
};

const EVENT_ICONS = {
  new_user_signup: 'user-plus',
  wallet_address_copied: 'copy',
  deposit_submitted: 'arrow-down',
  withdrawal_requested: 'arrow-up',
};

export const AdminNotificationProvider = ({ children }) => {
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    new_user_signup: true,
    wallet_address_copied: true,
    deposit_submitted: true,
    withdrawal_requested: true,
    sound_enabled: true,
    // Email notification settings
    email_enabled: false,
    email_new_user_signup: true,
    email_wallet_address_copied: false,
    email_deposit_submitted: true,
    email_withdrawal_requested: true,
    // Push notification settings
    push_enabled: false,
    push_new_user_signup: true,
    push_wallet_address_copied: false,
    push_deposit_submitted: true,
    push_withdrawal_requested: true,
  });
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [recentEvents, setRecentEvents] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      setPermissionGranted(true);
      return true;
    }

    if (Notification.permission === 'denied') {
      setPermissionGranted(false);
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setPermissionGranted(granted);
      return granted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((event) => {
    if (!permissionGranted || !notificationSettings.enabled) return;

    // Check if this event type is enabled
    if (!notificationSettings[event.event_type]) return;

    const title = EVENT_LABELS[event.event_type] || 'Admin Alert';
    let body = '';
    let icon = '/favicon.ico';

    switch (event.event_type) {
      case 'new_user_signup':
        body = `${event.user_name} (${event.user_email}) just signed up!`;
        break;
      case 'wallet_address_copied':
        body = `${event.user_name} copied ${event.crypto_type} wallet address`;
        break;
      case 'deposit_submitted':
        body = `${event.user_name} submitted a $${event.amount?.toLocaleString()} ${event.crypto_type} deposit`;
        break;
      case 'withdrawal_requested':
        body = `${event.user_name} requested $${event.amount?.toLocaleString()} ${event.crypto_type} withdrawal`;
        break;
      default:
        body = `New activity from ${event.user_name || event.user_email}`;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon,
        tag: event.id, // Prevents duplicate notifications
        requireInteraction: false,
        silent: !notificationSettings.sound_enabled,
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }, [permissionGranted, notificationSettings]);

  // Show in-app toast as fallback
  const showToastNotification = useCallback((event) => {
    if (!notificationSettings.enabled) return;
    if (!notificationSettings[event.event_type]) return;

    const title = EVENT_LABELS[event.event_type] || 'Admin Alert';
    let message = '';

    switch (event.event_type) {
      case 'new_user_signup':
        message = `${event.user_name} just signed up`;
        toast.success(message, { description: title });
        break;
      case 'wallet_address_copied':
        message = `${event.user_name} copied ${event.crypto_type} address`;
        toast.info(message, { description: title });
        break;
      case 'deposit_submitted':
        message = `$${event.amount?.toLocaleString()} ${event.crypto_type} deposit from ${event.user_name}`;
        toast.success(message, { description: title });
        break;
      case 'withdrawal_requested':
        message = `$${event.amount?.toLocaleString()} ${event.crypto_type} withdrawal from ${event.user_name}`;
        toast.warning(message, { description: title });
        break;
      default:
        toast.info(`Activity from ${event.user_name || event.user_email}`);
    }
  }, [notificationSettings]);

  // Handle incoming WebSocket event
  const handleIncomingEvent = useCallback((event) => {
    // Add to recent events
    setRecentEvents((prev) => [event, ...prev].slice(0, 50));
    setUnreadCount((prev) => prev + 1);

    // Show notifications
    if (permissionGranted && 'Notification' in window && Notification.permission === 'granted') {
      showBrowserNotification(event);
    } else {
      showToastNotification(event);
    }
  }, [permissionGranted, showBrowserNotification, showToastNotification]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('dex_admin_token');
    if (!token) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Build WebSocket URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/admin-notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Admin notification WebSocket connected');
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'admin_event' && data.event) {
            handleIncomingEvent(data.event);
          } else if (data.type === 'pong') {
            // Ping response received
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('Admin notification WebSocket closed:', event.code);
        setWsConnected(false);
        wsRef.current = null;

        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('Admin WebSocket error:', error);
        setWsConnected(false);
      };

      // Send ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      // Cleanup ping interval when WebSocket closes
      ws.addEventListener('close', () => clearInterval(pingInterval));

    } catch (error) {
      console.error('Error connecting to admin WebSocket:', error);
    }
  }, [handleIncomingEvent]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Intentional disconnect');
      wsRef.current = null;
    }
    setWsConnected(false);
  }, []);

  // Fetch notification settings from backend
  const fetchSettings = useCallback(async () => {
    try {
      const response = await adminAPI.getNotificationSettings();
      if (response.data.settings) {
        setNotificationSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  }, []);

  // Update notification settings
  const updateSettings = useCallback(async (newSettings) => {
    try {
      const response = await adminAPI.updateNotificationSettings(newSettings);
      if (response.data.settings) {
        setNotificationSettings(response.data.settings);
        toast.success('Notification settings updated');
      }
      return true;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast.error('Failed to update settings');
      return false;
    }
  }, []);

  // Clear unread count
  const clearUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Register service worker and subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    try {
      // Check if service worker and push are supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Push notifications not supported');
        toast.error('Push notifications are not supported in this browser');
        return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get VAPID public key from env or API
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        console.error('VAPID public key not configured');
        toast.error('Push notifications not configured');
        return false;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Convert subscription keys to base64
      const getKey = (subscription, name) => {
        const key = subscription.getKey(name);
        return key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null;
      };

      // Send subscription to server
      await adminAPI.subscribeToPush({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: getKey(subscription, 'p256dh'),
          auth: getKey(subscription, 'auth')
        }
      });

      setPushSubscribed(true);
      toast.success('Push notifications enabled!');
      console.log('Push subscription successful');
      return true;

    } catch (error) {
      console.error('Error subscribing to push:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to enable push notifications');
      }
      return false;
    }
  }, []);

  // Unsubscribe from push notifications
  const unsubscribeFromPush = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await adminAPI.unsubscribeFromPush(subscription.endpoint);
      }

      setPushSubscribed(false);
      toast.success('Push notifications disabled');
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Failed to disable push notifications');
      return false;
    }
  }, []);

  // Test push notification
  const testPushNotification = useCallback(async () => {
    try {
      await adminAPI.testPushNotification();
      toast.success('Test notification sent!');
      return true;
    } catch (error) {
      console.error('Error sending test push:', error);
      toast.error('Failed to send test notification');
      return false;
    }
  }, []);

  // Check if already subscribed to push
  const checkPushSubscription = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return false;
      }
      
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushSubscribed(!!subscription);
      return !!subscription;
    } catch (error) {
      console.error('Error checking push subscription:', error);
      return false;
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    const token = localStorage.getItem('dex_admin_token');
    if (token) {
      fetchSettings();
      requestPermission();
      connectWebSocket();
      checkPushSubscription();
      
      // Register service worker
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('/sw.js')
          .then(reg => console.log('Service Worker registered:', reg.scope))
          .catch(err => console.log('Service Worker registration failed:', err));
      }
    }

    return () => {
      disconnectWebSocket();
    };
  }, []);

  const value = {
    notificationSettings,
    permissionGranted,
    pushSubscribed,
    recentEvents,
    unreadCount,
    wsConnected,
    requestPermission,
    connectWebSocket,
    disconnectWebSocket,
    fetchSettings,
    updateSettings,
    clearUnreadCount,
    subscribeToPush,
    unsubscribeFromPush,
    testPushNotification,
    checkPushSubscription,
    EVENT_LABELS,
    EVENT_ICONS,
  };

  return (
    <AdminNotificationContext.Provider value={value}>
      {children}
    </AdminNotificationContext.Provider>
  );
};

export default AdminNotificationContext;
